import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getOrganisationDataWithId } from '../../utils/api/organisationAPI';
import { getUsersFromOrganisationId } from '../../utils/api/userAPI';
import { getOrganisationIntegrations } from '../../utils/api/integrationAPI';
import { getAssistantWithOrgId } from '../../utils/api/assistantAPI';
import { getLadgerDataWithOrg } from '../../utils/api/LedgerAPI';
import { convertToIndianTimeZone } from '../../utils/utils';
import { ShimmerLoader } from './ShimmerLoader';
import UnitCountSeven from "./UnitCountSeven";
import IncomeVsExpense from "./IncomeVsExpense";
import OverallReport from "./OverallReport";
import { Icon } from '@iconify/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { EditAssistantModal } from '../EditAssistantModal';
import { getPhoneNumberByOrgId } from '../../utils/api/phoneNumberAPI';

const OrganisationViewTab = () => {
    const user = useSelector((state) => state?.user?.user);
    const { id } = useParams();
    const naviagate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});
    const [orgUser, setOrgUser] = useState([]);
    const [orgUserPage, setOrgUserPage] = useState(1);
    const [orgUserNumberOfResult] = useState(10);
    const [orgUserTotalRecords, setOrgUserTotalRecords] = useState(0);
    const [orgUserTotalPages, setOrgUserTotalPages] = useState(1);
    const [organizationAssistant, setOrganizationAssistant] = useState([]);
    const [phoneNumber, setPhoneNumber] = useState([]);


    // Ledger state
    const [ledgerData, setLedgerData] = useState([]);
    const [ledgerPage, setLedgerPage] = useState(1);
    const [ledgerLimit] = useState(10);
    const [ledgerTotalRecords, setLedgerTotalRecords] = useState(0);
    const [ledgerFilters, setLedgerFilters] = useState({
        type: 'all',
        source: 'all',
        dateRange: [null, null],
    });
    const [tempLedgerFilters, setTempLedgerFilters] = useState({
        type: 'all',
        source: 'all',
        dateRange: [null, null],
    });
    const [selectedAssistantId, setSelectedAssistantId] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [activeTab, setActiveTab] = useState('Agent'); // or 'LLM' by default
    const [selectedAssistantDetail, setSelectedAssistantDetail] = useState(null);

    // This should be triggered when clicking the button
    const handleEditClick = async (id) => {
        setSelectedAssistantId(id);
        setShowEditModal(true);

        // Optional: fetch assistant details here if not already available
        const res = await fetch(`/api/assistant/${id}`); 
        const data = await res.json();
        setSelectedAssistantDetail(data);
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setSelectedAssistantId(null);
        setSelectedAssistantDetail(null);
        setActiveTab('Agent');
    };

    const formatDate = (date) => date?.toISOString().split('T')[0];

    const applyLedgerFilters = () => {
        setLedgerFilters(tempLedgerFilters);
        setLedgerPage(1);
    };
    useEffect(() => {
        if (id) {
          getPhoneNumberByOrgId(id).then((data) => setPhoneNumber(data?.phoneNumbers))
        }
      }, [id]);      

    // Fetch data hooks
    useEffect(() => {
        if (!id) return;
        const fetchOrganisationData = async () => {
            try {
                setLoading(true);
                const response = await getOrganisationDataWithId(id);
                setData(response["organization"]);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        fetchOrganisationData();
    }, [id]);

    // useEffect(() => {


    useEffect(() => {
        if (!id) return;
        const fetchAssistants = async () => {
            try {
                const response = await getAssistantWithOrgId(id);
                setOrganizationAssistant(response?.assistants || []);
            } catch (e) {
                console.error(e);
            }
        };
        fetchAssistants();
    }, [id]);

    useEffect(() => {
        if (!id) return;
        const fetchUsers = async () => {
            try {
                const response = await getUsersFromOrganisationId(id, orgUserPage, orgUserNumberOfResult);
                setOrgUser(response["users"]);
                const total = Math.ceil(response["total"] / orgUserNumberOfResult);
                setOrgUserTotalPages(total > 0 ? total : 1);
            } catch (e) {
                console.error(e);
            }
        };
        fetchUsers();
    }, [id, orgUserPage, orgUserNumberOfResult]);

    useEffect(() => {
        if (!id) return;

        const fetchLedger = async () => {
            try {
                const { type, source, dateRange } = ledgerFilters;

                const response = await getLadgerDataWithOrg({
                    organizationId: id,
                    page: ledgerPage,
                    limit: ledgerLimit,
                    type: type === 'all' ? '' : type,
                    source: source === 'all' ? '' : source,
                    startedAt: dateRange[0] ? formatDate(dateRange[0]) : '',
                    endedAt: dateRange[1] ? formatDate(dateRange[1]) : '',
                });

                setLedgerData(response?.ledger || []);
                setLedgerTotalRecords(response?.total || 0);
            } catch (e) {
                console.error("Ledger Fetch Error:", e);
            }
        };

        fetchLedger();
    }, [id, ledgerPage, ledgerFilters]);
    // Memoized pagination handlers to prevent unnecessary re-renders
    const handlePreviousPage = useCallback(() => {
        setOrgUserPage((prev) => Math.max(prev - 1, 1));
    }, []);

    const handleNextPage = useCallback(() => {
        setOrgUserPage((prev) => Math.min(prev + 1, orgUserTotalPages));
    }, [orgUserTotalPages]);

    const handlePageClick = useCallback((pageNumber) => {
        setOrgUserPage(pageNumber);
    }, []);

    if (loading) {
        return (
            <div className="col" style={{ height: "80vh" }}>
                <div className="card p-0 overflow-hidden position-relative radius-12 h-100">
                    <div className="card-header py-24 px-24 bg-base border border-end-0 border-start-0 border-top-0">
                        <h6 className="text-lg mb-0">Organisation Details</h6>
                    </div>
                    <ShimmerLoader />
                </div>
            </div>
        );
    }

    return (
        <div className="col card" style={{ height: 'auto' }}>
            <div className="card-header py-24 px-24 bg-base border border-end-0 border-start-0 border-top-0">
                <div className='flex justify-content-between'>
                    <h6 className="text-lg mb-0 flex items-center gap-2">
                        {data?.name} - <span>{data?.id}</span>
                        {/* {data?.isSubscribed ? (
                            <Icon
                                icon="mdi:check-circle"
                                className="text-green-500 text-xl cursor-pointer"
                                style={{ color: 'var(--primary-500)' }}
                                title="Subscribed"
                            />
                        ) : (
                            <Icon
                                icon="mdi:close-circle"
                                className="text-red-500 text-xl cursor-pointer"
                                style={{ color: 'var(--danger-500)' }}
                                title="Not Subscribed"
                            />
                        )} */}
                    </h6>


                    <h6 className='text-lg mb-0'>Created At : {convertToIndianTimeZone(data?.createdAt)}</h6>
                </div>

            </div>
            <div className=" p-0 overflow-hidden position-relative radius-12 ">
                <div className='mb-10'>
                    <UnitCountSeven />
                </div>
                <div className='p-0 overflow-hidden position-relative radius-12'>
                    <IncomeVsExpense data={data} />
                    {/* <OverallReport /> */}
                </div>
                {/* Organisation users start */}
                <div className=" p-0 radius-12 w-100 h-100 mt-6">
                    <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-column gap-3">
                        <div className="fw-semibold fs-5">Organisation Users</div>
                    </div>

                    <div className="card-body px-24 pt-10 pb-0">
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">ID</th>
                                        <th scope="col">Created At</th>
                                        <th scope="col">Full Name</th>
                                        <th scope="col">Email</th>
                                        <th scope="col">Phone Number</th>
                                        <th scope="col" className="text-center">Status</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {orgUser.length > 0 ? (
                                        orgUser.map((item) => (
                                            <tr key={item.id}>
                                                <td>{ item.id}</td>
                                                <td>{convertToIndianTimeZone(item.createdAt)}</td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {item.displayName}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {item.email}
                                                    </span>
                                                </td>
                                                <td>{item.phone}</td>
                                                <td className="text-center">
                                                    {item.status ? (
                                                        <span className="badge text-sm fw-semibold text-success-600 bg-success-100 px-20 py-9 radius-4 text-white">
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="badge text-sm fw-semibold text-danger-600 bg-danger-100 px-20 py-9 radius-4 text-white">
                                                            InActive
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6}>
                                                <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '40vh' }}>
                                                    <Icon icon="icon-park-outline:file-search" className="text-3xl mb-3 text-muted" />
                                                    <p className="text-secondary-light text-md mb-0">No users found</p>
                                                    <button
                                                        type="button"
                                                        className="btn btn-info-600 radius-8 px-20 py-11 mt-3"
                                                        style={{ minWidth: '180px' }}
                                                    >
                                                        Add New User
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination like ledger */}
                        {orgUser.length > 0 && (
                            <div
                                className="d-flex  align-items-center py-20 flex-wrap gap-2"
                                style={{
                                    justifyContent: 'flex-end',
                                }}
                            >

                                {/* <span className="text-md text-muted">
                                    Showing {(orgUserPage - 1) * orgUserNumberOfResult + 1} to {Math.min(orgUserPage * orgUserNumberOfResult, orgUserTotalRecords)} of {orgUserTotalRecords} entries
                                </span> */}
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm radius-8 d-flex align-items-center justify-content-center" 
                                        disabled={orgUserPage === 1}
                                        onClick={handlePreviousPage}
                                    >

                                        <Icon icon="ep:arrow-left-bold" />
                                        <span className='ms-1'>Prev</span> 
                                    </button>
                                    <button
                                        className="btn btn-sm radius-8 d-flex align-items-center justify-content-center"
                                        disabled={orgUserPage === orgUserTotalPages}
                                        onClick={handleNextPage}
                                    >
                                        <span className="me-1">Next</span>
                                        <Icon icon="ep:arrow-right-bold" />
                                    </button>

                                </div>
                            </div>
                        )}
                    </div>
                </div>
                {/* Organisation users end */}

                {/* Organisation Assistant start */}
                <div className="card p-0 radius-12 w-100 h-100 mt-6">
                    <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-column gap-3">
                        <div className="fw-semibold fs-5">Organisation Assistant</div>
                    </div>

                    <div className="card-body px-24 pt-10 pb-0">
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-10">
                                <thead>
                                    <tr>
                                        <th scope="col">ID</th>
                                        <th scope="col">Created At</th>
                                        <th scope="col">Name</th>

                                        <th scope="col" className="text-center">Status</th>
                                        <th scope='col' className='text-center'>Actions</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {organizationAssistant.length > 0 ? (
                                        organizationAssistant.map((item) => (
                                            <tr key={item.id}>
                                                <td>{ item.id}</td>
                                                <td>{convertToIndianTimeZone(item.createdAt)}</td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {item.name}
                                                    </span>
                                                </td>

                                                <td className="text-center">
                                                    {item.status ? (
                                                        <span className="badge text-sm fw-semibold text-success-600 bg-success-100 px-20 py-9 radius-4 text-white">
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="badge text-sm fw-semibold text-danger-600 bg-danger-100 px-20 py-9 radius-4 text-white">
                                                            InActive
                                                        </span>
                                                    )}
                                                </td>

                                                <td className="text-center">
                                                    <div className="d-flex align-items-center gap-10 justify-content-center">

                                                        <button
                                                            type="button"
                                                            onClick={() => handleEditClick(item.id)}
                                                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        >
                                                            <Icon icon="lucide:edit" className="menu-icon" />
                                                        </button>

                                                    </div>
                                                </td>

                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={6}>
                                                <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '40vh' }}>
                                                    <Icon icon="icon-park-outline:file-search" className="text-3xl mb-3 text-muted" />
                                                    <p className="text-secondary-light text-md mb-0">No Assistant found</p>

                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>


                    </div>
                </div>
                {/* Organisation users end */}
                {/* Organisation Assistant start */}
                <div className="card p-0 radius-12 w-100 h-100 mt-6">
                    <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-column gap-3">
                        <div className="fw-semibold fs-5">Phone Number</div>
                    </div>

                    <div className="card-body px-24 pt-10 pb-0">
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-10">
                                <thead>
                                    <tr>
                                        <th scope="col">Phone Number</th>
                                        <th scope='col' >Created At</th>
                                        <th scope="col" >Provider</th>
                                        <th scope="col" >Domain</th>
                                        <th scope="col">Status</th>
                                        
                                    </tr>
                                </thead>

                                <tbody>
                                    {phoneNumber.length > 0 ? (
                                        phoneNumber.map((item) => (
                                            <tr key={item.id}>
                                                <td >{item.did}</td>
                                                <td >{convertToIndianTimeZone(item.createdAt)}</td>
                                                <td >{item.provider}</td>
                                                <td>{item.domain}</td>
                                                
                                                

                                                <td>
                                                    {item.status ? (
                                                        <span className="badge text-sm fw-semibold text-success-600 bg-success-100 px-20 py-9 radius-4 text-white">
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="badge text-sm fw-semibold text-danger-600 bg-danger-100 px-20 py-9 radius-4 text-white">
                                                            InActive
                                                        </span>
                                                    )}
                                                </td>
                                               

                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4}>
                                                <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '40vh' }}>
                                                    <Icon icon="icon-park-outline:file-search" className="text-3xl mb-3 text-muted" />
                                                    <p className="text-secondary-light text-md mb-0">No Phone Number found</p>

                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>


                    </div>
                </div>
                {/* Organisation users end */}

                {/* Ledger Table */}
                <div className="card p-0 radius-12 w-100 h-100 mt-6 ">
                    <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-column gap-3">
                        <div className="fw-semibold fs-5">Ledger History</div>
                        <div className="d-flex flex-wrap gap-3 align-items-end justify-content-between">
                            <div className="d-flex flex-column flex-1">
                                <label className="text-sm text-muted mb-1">Transaction Type</label>
                                <select
                                    value={tempLedgerFilters.type}
                                    onChange={(e) => setTempLedgerFilters(prev => ({ ...prev, type: e.target.value }))}
                                    style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 12px', minWidth: '140px' }}
                                >
                                    <option value="all">All</option>
                                    <option value="credit">Credit</option>
                                    <option value="debit">Debit</option>
                                </select>
                            </div>

                            <div className="d-flex flex-column flex-1">
                                <label className="text-sm text-muted mb-1">Transaction Source</label>
                                <select
                                    value={tempLedgerFilters.source}
                                    onChange={(e) => setTempLedgerFilters(prev => ({ ...prev, source: e.target.value }))}
                                    style={{ border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 12px', minWidth: '140px' }}
                                >
                                    <option value="all">All</option>
                                    <option value="purchase">Purchase</option>
                                    <option value="call">Call</option>
                                    <option value="grace">Grace</option>
                                    <option value="promo">Promo</option>
                                    <option value="manual">Manual</option>
                                </select>
                            </div>

                            <div className="d-flex flex-column">
                                <label className="text-sm text-muted mb-1">Date Range</label>

                                <DatePicker
                                    selectsRange
                                    startDate={tempLedgerFilters.dateRange[0]}
                                    endDate={tempLedgerFilters.dateRange[1]}
                                    onChange={(update) => setTempLedgerFilters(prev => ({ ...prev, dateRange: update }))}
                                    placeholderText="Select date range"
                                    className="datepicker_custom_form-control"
                                    maxDate={new Date()}
                                    popperPlacement="bottom-start"
                                    calendarClassName="custom-datepicker"
                                />


                            </div>

                            <button
                                className="btn btn-primary btn-sm radius-8 d-flex align-items-center"
                                style={{ height: '47px' }}
                                onClick={applyLedgerFilters}
                            >
                                <Icon icon="ic:baseline-filter-alt" className="me-1" />
                                Apply Filter
                            </button>
                        </div>
                    </div>

                    <div className="card-body px-24 pt-10 pb-0 ">
                        <div className="table-responsive">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th>Source</th>
                                        <th>Duration</th>
                                        <th>Description</th>
                                        <th>Time Stamp</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ledgerData.length > 0 ? (
                                        ledgerData.map((entry) => (
                                            <tr key={entry.id}>
                                                <td>{entry.type || '-'}</td>
                                                <td>{entry.source || '-'}</td>
                                                <td>
                                                    {typeof entry.seconds === 'number'
                                                        ? `${Math.floor(entry.seconds / 60)}m ${entry.seconds % 60}s`
                                                        : '-'}
                                                </td>
                                                <td>{entry.description || '-'}</td>
                                                <td>{convertToIndianTimeZone(entry.createdAt)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5}>
                                                <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '40vh' }}>
                                                    <Icon icon="icon-park-outline:file-search" className="text-3xl mb-3 text-muted" />
                                                    <p className="text-secondary-light text-md mb-0">No ledger records found</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {ledgerData.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center py-20 flex-wrap gap-2">
                                <span className="text-md text-muted">
                                    Showing {(ledgerPage - 1) * ledgerLimit + 1} to {Math.min(ledgerPage * ledgerLimit, ledgerTotalRecords)} of {ledgerTotalRecords} entries
                                </span>
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-sm radius-8 d-flex align-items-center justify-content-center"
                                        disabled={ledgerPage === 1}
                                        onClick={() => setLedgerPage(prev => Math.max(prev - 1, 1))}
                                    >
                                        <Icon icon="ep:arrow-left-bold" /> <span className='me-1'>Prev
                                            </span>
                                    </button>
                                    <button
                                        className="btn btn-sm radius-8 d-flex align-items-center justify-content-center"
                                        disabled={ledgerPage * ledgerLimit >= ledgerTotalRecords}
                                        onClick={() => setLedgerPage(prev => prev + 1)}
                                    >
                                        <span className='ms-1'>Next
                                            </span> <Icon icon="ep:arrow-right-bold" />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>


                {showEditModal && (
                    <EditAssistantModal
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        assistantDetail={selectedAssistantDetail}
                        assistantId={selectedAssistantId}
                        onClose={handleCloseModal}
                    />
                )}


            </div>
        </div>
    );
};

export default OrganisationViewTab;
